import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { RootState } from "stores/Store";
import { User } from "interfaces/user";
import { setShowNavbar, setShowFriends } from "stores/uiState/UiStateSlice";
import Login from "components/Login";
import { getRankImage } from "utils/uiStateUtils";
import { useNavigate } from "react-router-dom";

function Leaderboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setShowNavbar(true));
    dispatch(setShowFriends(true));
  }, []);

  const currentUser = useSelector(
    (state: RootState) => state.userReducer.user,
    shallowEqual,
  );

  const [users, setUsers] = useState<User[]>([]);
  const [currentUserPosition, setCurrentUserPosition] = useState<number | null>(
    null,
  );

  const fetchTop100Users = async (): Promise<User[]> => {
    if (!currentUser) {
      return [];
    }
    const db = getFirestore();
    const usersQuery = query(
      collection(db, "users"),
      orderBy("currentElo", "desc"),
      limit(100),
    );
    const querySnapshot = await getDocs(usersQuery);

    const usersFetched: User[] = querySnapshot.docs.map((doc) => ({
      uid: doc.id,
      username: doc.data().username,
      currentElo: doc.data().currentElo,
    }));

    return usersFetched;
  };

  const fetchCurrentUserRank = async (): Promise<number> => {
    if (!currentUser) {
      return -1;
    }
    const db = getFirestore();
    const usersAboveCurrentUserQuery = query(
      collection(db, "users"),
      orderBy("currentElo", "desc"),
      where("currentElo", ">", currentUser?.currentElo),
    );
    const usersAboveSnapshot = await getDocs(usersAboveCurrentUserQuery);
    return usersAboveSnapshot.size + 1;
  };

  useEffect(() => {
    const fetchData = async () => {
      const topUsers = await fetchTop100Users();
      setUsers(topUsers);

      const position = topUsers.findIndex(
        (user) => user.uid === currentUser?.uid,
      );
      if (position !== -1) {
        setCurrentUserPosition(position + 1);
      } else {
        const rank = await fetchCurrentUserRank();
        setCurrentUserPosition(rank);
      }
    };
    if (currentUser) {
      fetchData();
    }
  }, [currentUser?.uid]);

  if (!currentUser) {
    return <Login message="Please login to see the Leaderboard" />;
  }

  return (
    <section className="Leaderboard">
      <div className="heroContainer">
        <div className="headerContainer">
          <h1 className="title">Leaderboard</h1>
          <h2 className="headline">
            Where champions rise and legends are born
          </h2>
        </div>
      </div>
      <div className="usersContainer">
        {users.map((user, index) => (
          <motion.div
            className="userCard"
            key={user.username}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(`/user/${user.uid}`)}
          >
            <img src={getRankImage(index + 1)} alt="Rank" />
            <span>
              <p>
                <b>{index + 1}</b>.
              </p>
              <p>
                {user?.username} - {user?.currentElo} LP
              </p>
            </span>
          </motion.div>
        ))}
      </div>
      {currentUser?.uid && (
        <>
          <div className="separateContainer">
            <div className="separateLine">&nbsp;</div>
            <div className="separateLine">&nbsp;</div>
            <div className="separateLine">&nbsp;</div>
          </div>
          <motion.div
            className="playerRankCard"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(`/user/${currentUser.uid}`)}
          >
            <img src={getRankImage(currentUserPosition || 0)} alt="Rank" />
            <span>
              {!currentUser?.rankedGamesPlayed ? (
                <p>
                  <b>Unranked</b> — Play a ranked game to appear on the
                  leaderboard
                </p>
              ) : (
                <>
                  <p>
                    <b>{currentUserPosition}</b>.
                  </p>
                  <p>
                    {currentUser?.username} - {currentUser?.currentElo} LP
                  </p>
                </>
              )}
            </span>
          </motion.div>
        </>
      )}
    </section>
  );
}

export default Leaderboard;
